import { ErrorHandler, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AnnouncementModel } from '@app/core/model/announcement.model';
import { LedgerModel } from '@app/core/model/ledger.model';
import { RecognitionApiService } from '@app/core/service/api/recognition.api.service';
import { WhatsNewModalComponent } from '@app/modals/whats-new-modal/whats-new-modal.component';
import { MongoStoredObject } from '@app/types/mongo-stored-object';
import { showModal } from '@app/utils/modal';
import { IPsychologicalSurvey } from '@backend/models/types/psychological-survey';
import { IPsychologicalSurveyQuestion } from '@backend/models/types/psychological-survey-question';
import { ModalController } from '@ionic/angular';
import { BehaviorSubject, catchError, combineLatest, of } from 'rxjs';
import { AnnouncementApiService } from './api/announcement.api.service';
import { LedgerApiService } from './api/ledger.api.service';
import { PsychologicalSurveyApiService } from './api/psychologicalSurvey.api.service';

@Injectable()
export class WhatsNewService {
  private _announcements$ = new BehaviorSubject<{
    items: AnnouncementModel[];
    count: number;
    totalCount: number;
  }>({ items: [], count: 0, totalCount: 0 });

  private _recognitions$ = new BehaviorSubject<{
    items: LedgerModel[];
    count: number;
    totalCount: number;
  }>({ items: [], count: 0, totalCount: 0 });

  private _surveys$ = new BehaviorSubject<{
    items: {
      survey: MongoStoredObject<IPsychologicalSurvey>;
      questions: MongoStoredObject<IPsychologicalSurveyQuestion>[];
    }[];
    count: number;
    totalCount: number;
  }>({ items: [], count: 0, totalCount: 0 });

  private _recognitionReminders$ = new BehaviorSubject<{
    items: { title: string }[];
    count: number;
    totalCount: number;
  }>({ items: [], count: 0, totalCount: 0 });

  public announcements$ = this._announcements$.asObservable();
  public recognitions$ = this._recognitions$.asObservable();
  public surveys$ = this._surveys$.asObservable();
  public recognitionReminders$ = this._recognitionReminders$.asObservable();

  public constructor(
    private readonly _ledgerApiService: LedgerApiService,
    private readonly _announcementApiService: AnnouncementApiService,
    private readonly _psychologicalSurveyApiService: PsychologicalSurveyApiService,
    private readonly _recognitionApiService: RecognitionApiService,
    private readonly _modalCtrl: ModalController,
    private readonly _router: Router,
    private readonly _errorHandler: ErrorHandler
  ) {}

  public checkWhatsNew(showWhatsNewModal = true) {
    combineLatest([
      this._ledgerApiService
        .getUnreadLedgers()
        .pipe(
          catchError((error) => {
            this._errorHandler.handleError(error);
            return of({ items: [], count: 0, totalCount: 0 });
          })
        ),
      this._announcementApiService
        .getCriticalUnacknowledgedAnnouncements()
        .pipe(
          catchError((error) => {
            this._errorHandler.handleError(error);
            return of({ items: [], count: 0, totalCount: 0 });
          })
        ),
      this._psychologicalSurveyApiService
        .getUnansweredPsychologicalSurveys({
          query: {}
        })
        .pipe(
          catchError((error) => {
            this._errorHandler.handleError(error);
            return of({ items: [], count: 0, totalCount: 0 });
          })
        ),
      this._recognitionApiService
        .getRecognitionReminders()
        .pipe(
          catchError((error) => {
            this._errorHandler.handleError(error);
            return of({ items: [], count: 0, totalCount: 0 });
          })
        )
    ]).subscribe(
      async ([
        unreadRecognitions,
        criticalAnnouncements,
        unansweredSurveys,
        recognitionReminders
      ]) => {
        this._recognitions$.next(unreadRecognitions);
        this._announcements$.next(criticalAnnouncements);
        this._surveys$.next(unansweredSurveys);
        this._recognitionReminders$.next(recognitionReminders);

        if (showWhatsNewModal) {
          const showViewRecognitionPage =
            unreadRecognitions.count === 1 &&
            criticalAnnouncements.count === 0 &&
            unansweredSurveys.count === 0 &&
            recognitionReminders.count === 0;

          if (showViewRecognitionPage) {
            const tenantId = unreadRecognitions.items[0].tenant._id.toString();
            this._router.navigate([`/i/${tenantId}/view-recognition`], {
              queryParams: {
                post: unreadRecognitions.items[0]._id.toString(),
                isForMe: 'true',
                recipient: unreadRecognitions.items[0].recipient._id.toString()
              }
            });
          } else if (
            unreadRecognitions.count ||
            criticalAnnouncements.count ||
            unansweredSurveys.count ||
            recognitionReminders.count
          ) {
            await showModal(
              {
                component: WhatsNewModalComponent,
                modalName: 'WhatsNewModalComponent'
              },
              this._modalCtrl,
              {
                canDismiss: async (_, role) => role === 'confirm',
                useTopModalAsParent: true
              }
            );
          }
        }
      }
    );
  }

  public removeItemFromTheList(
    itemId: string,
    type: 'announcement' | 'recognition' | 'survey' | 'recognitionReminder'
  ) {
    switch (type) {
      case 'announcement': {
        const announcementItems = this._announcements$.value.items.filter(
          (a) => a._id !== itemId
        );
        this._announcements$.next({
          items: announcementItems,
          count: announcementItems.length,
          totalCount: Math.max(
            this._announcements$.value.totalCount - 1,
            announcementItems.length
          )
        });
        break;
      }
      case 'recognition': {
        const recognitionItems = this._recognitions$.value.items.filter(
          (a) => a._id.toString() !== itemId
        );
        this._recognitions$.next({
          items: recognitionItems,
          count: recognitionItems.length,
          totalCount: Math.max(
            this._recognitions$.value.totalCount - 1,
            recognitionItems.length
          )
        });
        break;
      }
      case 'survey': {
        const surveyItems = this._surveys$.value.items.filter(
          (a) => a.survey._id.toString() !== itemId
        );
        this._surveys$.next({
          items: surveyItems,
          count: surveyItems.length,
          totalCount: Math.max(
            this._surveys$.value.totalCount - 1,
            surveyItems.length
          )
        });
        break;
      }
      case 'recognitionReminder':
        this._recognitionReminders$.next({
          items: [],
          count: 0,
          totalCount: 0
        });
        break;
    }
    this.checkWhatsNew(false);
  }
}
